import { createSlice } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import {
  deletePortfolio,
  getCompanyOptions,
  getContractedRateTypes,
  getDueDiligenceData,
  getFacilityStructuresList,
  getFiftyStatesList,
  getOutsourcedCompanyCategories,
  getOwnershipTypes,
  getPermissions,
  getPlatformVendors,
  getPortfolioData,
  getPrograms,
  getTransitionDefaultFacility,
  getTransitionDefaultPortfolio,
  getTransitionPageData,
  getTransitionsPortfolioData,
  getTransitionsStaffSubTypes,
  getTransitionsStaffTypes,
  getTransitionStatuses,
  getTransitionVendors,
  updateCanceledStatus,
  getVehicleDepartmentTypes,
  getTransportationUserAccessTypes,
} from '../../api/transitions';

// ----------------------------------------------------------------------
const initialState = {
  error: null,
  data: {
    portfolios: [],
    defaultFacility: null,
    defaultPortfolio: null,
    statusConfig: null,
    statusConfigSortedKeys: [],
    isCanceled: null,
    userPermissions: {},
    staffTypes: [],
    staffSubTypes: [],
    fiftyStates: [],
    facilityStructures: [],
    companyOptions: [],
    outsourcedCompanyCategoriesList: [],
    ownershipTypesList: [],
    vehicleDepartmentsTypesList: [],
    transportationUserAccessTypesList: [],
    transitionVendors: {},
    platformVendors: [],
    programs: {},
    currentStatusByPortfolio: {},
    contractedRateTypesList: [],
  },
};

const slice = createSlice({
  name: 'transitions',
  initialState,
  reducers: {
    // HAS ERROR
    hasError(state, action) {
      state.error = action.payload.error;
      enqueueSnackbar(action.payload.errorMessage, { variant: 'error' });
    },

    getPortfoliosDataSuccess(state, action) {
      state.data.portfolios = action.payload;
    },

    getUserPermissionsSuccess(state, action) {
      state.data.userPermissions = { ...state.data.userPermissions, ...action.payload };
    },

    getDefaultPortfolioSuccess(state, action) {
      state.data.defaultPortfolio = action.payload;
    },

    getDefaultFacilitySuccess(state, action) {
      state.data.defaultFacility = action.payload;
    },

    deletePortfolioByIdSuccess(state, action) {
      state.data.portfolios = action.payload;
    },

    getStatusConfigSuccess(state, action) {
      state.data.statusConfig = action.payload;
      state.data.statusConfigSortedKeys = Object.values(action.payload)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((x) => x.id);
    },

    getIsCanceledDataSuccess(state, action) {
      state.data.isCanceled = action.payload.isCanceled;
    },
    getStatusIdSuccess(state, action) {
      const { id, statusId } = action.payload;
      if (id == null) return;

      state.data.currentStatusByPortfolio = {
        ...state.data.currentStatusByPortfolio,
        [id]: statusId,
      };
    },
    getStaffTypesSuccess(state, action) {
      state.data.staffTypes = action.payload;
    },
    getStaffSubTypesSuccess(state, action) {
      state.data.staffSubTypes = action.payload;
    },
    getFiftyStatesSuccess(state, action) {
      state.data.fiftyStates = action.payload;
    },
    getFacilityStructuresSuccess(state, action) {
      state.data.facilityStructures = action.payload;
    },
    getOutsourcedCompanyCategoriesSuccess(state, action) {
      state.data.outsourcedCompanyCategoriesList = action.payload;
    },

    getTransitionVendorsSuccess(state, action) {
      state.data.transitionVendors = { ...state.data.transitionVendors, ...action.payload };
    },

    getPlatformVendorsSuccess(state, action) {
      state.data.platformVendors = action.payload;
    },

    getOwnershipTypesSuccess(state, action) {
      state.data.ownershipTypesList = action.payload;
    },

    getVehicleDepartmentTypesSuccess(state, action) {
      state.data.vehicleDepartmentsTypesList = action.payload;
    },

    getTransportationUserAccessTypesSuccess(state, action) {
      state.data.transportationUserAccessTypesList = action.payload;
    },

    getProgramsSuccess(state, action) {
      const { companyId, programs } = action.payload;
      state.data.programs = {
        ...state.data.programs,
        [companyId]: programs,
      };
    },

    getCompanyOptionsSuccess(state, action) {
      state.data.companyOptions = action.payload;
    },

    getContractedRateTypesSuccess(state, action) {
      state.data.contractedRateTypesList = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export function getPortfoliosData() {
  return async (dispatch) => {
    try {
      const response = await getTransitionsPortfolioData();
      dispatch(slice.actions.getPortfoliosDataSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to load portfolios' }));
    }
  };
}

export function getUserPermissions(portfolioId) {
  return async (dispatch) => {
    try {
      const response = await getPermissions(portfolioId);
      dispatch(slice.actions.getUserPermissionsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to get user permissions' }));
    }
  };
}

export function getDefaultPortfolio() {
  return async (dispatch) => {
    try {
      const response = await getTransitionDefaultPortfolio();
      dispatch(slice.actions.getDefaultPortfolioSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to load default portfolio' }));
    }
  };
}

export function getDefaultFacility() {
  return async (dispatch) => {
    try {
      const response = await getTransitionDefaultFacility();
      dispatch(slice.actions.getDefaultFacilitySuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to load default facility' }));
    }
  };
}

export function deletePortfolioById(portfolioId) {
  return async (dispatch) => {
    try {
      const response = await deletePortfolio(portfolioId);
      dispatch(slice.actions.deletePortfolioByIdSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to delete portfolio' }));
    }
  };
}

export function getStatusConfig() {
  return async (dispatch) => {
    try {
      const response = await getTransitionStatuses();
      dispatch(slice.actions.getStatusConfigSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to load portfolio statuses' }));
    }
  };
}

export function getInitialDueDiligenceData(id, facilityId) {
  return async (dispatch) => {
    try {
      const response = await getPortfolioData(id, facilityId);
      dispatch(slice.actions.getIsCanceledDataSuccess(response.data));
      dispatch(slice.actions.getStatusIdSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to load Initial Due Diligence data' }));
      return null;
    }
  };
}
export function getReduxDueDiligenceData(id, facilityId) {
  return async (dispatch) => {
    try {
      const response = await getDueDiligenceData(id, facilityId);
      dispatch(slice.actions.getIsCanceledDataSuccess(response.data));
      dispatch(slice.actions.getStatusIdSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to load Due Diligence data' }));
      return null;
    }
  };
}
export function getReduxTransitionPageData(id) {
  return async (dispatch) => {
    try {
      const response = await getTransitionPageData(id);
      dispatch(slice.actions.getIsCanceledDataSuccess(response.data));
      dispatch(slice.actions.getStatusIdSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to load Transitions page data' }));
      return null;
    }
  };
}
export function updateIsCanceled(id, isCanceled) {
  return async (dispatch) => {
    try {
      const response = await updateCanceledStatus(id, isCanceled);
      dispatch(slice.actions.getIsCanceledDataSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to update portfolio status' }));
      return null;
    }
  };
}

export function getStaffTypes() {
  return async (dispatch) => {
    try {
      const response = await getTransitionsStaffTypes();
      dispatch(slice.actions.getStaffTypesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to load staff types' }));
    }
  };
}

export function getStaffSubTypes() {
  return async (dispatch) => {
    try {
      const response = await getTransitionsStaffSubTypes();
      dispatch(slice.actions.getStaffSubTypesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to load staff sub types' }));
    }
  };
}

export function getFiftyStates() {
  return async (dispatch) => {
    try {
      const response = await getFiftyStatesList();
      dispatch(slice.actions.getFiftyStatesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to load fifty states list' }));
    }
  };
}

export function getFacilityStructures() {
  return async (dispatch) => {
    try {
      const response = await getFacilityStructuresList();
      dispatch(slice.actions.getFacilityStructuresSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to load facility structures list' }));
    }
  };
}

export function outsourcedCompanyCategories() {
  return async (dispatch) => {
    try {
      const response = await getOutsourcedCompanyCategories();
      dispatch(slice.actions.getOutsourcedCompanyCategoriesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to get outsourced company categories list' }));
    }
  };
}

export function getTransitionVendorsList(companyId) {
  return async (dispatch) => {
    try {
      const response = await getTransitionVendors(companyId);
      dispatch(slice.actions.getTransitionVendorsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to get Transitions vendors list' }));
    }
  };
}

export function getPlatformVendorsList() {
  return async (dispatch) => {
    try {
      const response = await getPlatformVendors();
      dispatch(slice.actions.getPlatformVendorsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to get Purchasing and Rental vendors list' }));
    }
  };
}

export function getOwnershipTypesList() {
  return async (dispatch) => {
    try {
      const response = await getOwnershipTypes();
      dispatch(slice.actions.getOwnershipTypesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to get ownership types list' }));
    }
  };
}

export function getProgramsList(companyId) {
  return async (dispatch) => {
    try {
      const response = await getPrograms(companyId);
      const payload = response.data;
      const companyPrograms = payload?.[companyId] ?? [];
      dispatch(slice.actions.getProgramsSuccess({ companyId, programs: companyPrograms }));
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to get programs list' }));
    }
  };
}

export function getCompanyOptionsList() {
  return async (dispatch) => {
    try {
      const response = await getCompanyOptions();
      dispatch(slice.actions.getCompanyOptionsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to load company options' }));
    }
  };
}

export function getContractedRateTypesList() {
  return async (dispatch) => {
    try {
      const response = await getContractedRateTypes();
      dispatch(slice.actions.getContractedRateTypesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to get contracted rate types list' }));
    }
  };
}

export function getVehicleDepartmentTypesList() {
  return async (dispatch) => {
    try {
      const response = await getVehicleDepartmentTypes();
      dispatch(slice.actions.getVehicleDepartmentTypesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to get vehicle department types list' }));
    }
  };
}

export function getTransportationUserAccessTypesList() {
  return async (dispatch) => {
    try {
      const response = await getTransportationUserAccessTypes();
      dispatch(slice.actions.getTransportationUserAccessTypesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError({ error, errorMessage: 'Failed to get transportation user access types list' }));
    }
  };
}
